import { useState, useEffect, useCallback } from 'react'
import { libraryService, LibraryDocument, LibraryTaxonomies, ListLibraryParams } from '@/services/libraryService'

interface FilterState {
  search?: string
  taxonomyIds: string[]
  documentTypes: string[]
}

interface UseLibraryReturn {
  documents: LibraryDocument[]
  loading: boolean
  error: string | null
  page: number
  totalPages: number
  hasMore: boolean
  filters: FilterState
  taxonomies: LibraryTaxonomies | null
  toggleTaxonomy: (id: string) => void
  toggleDocumentType: (type: string) => void
  setSearch: (search: string | undefined) => void
  clearFilters: () => void
  loadMore: () => void
  refresh: () => void
}

export function useLibrary(): UseLibraryReturn {
  const [documents, setDocuments] = useState<LibraryDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [taxonomies, setTaxonomies] = useState<LibraryTaxonomies | null>(null)
  const [filters, setFilters] = useState<FilterState>({
    taxonomyIds: [],
    documentTypes: [],
  })

  // Serialize filter state into API params
  const buildParams = useCallback((currentPage: number): ListLibraryParams => {
    const params: ListLibraryParams = {
      page: currentPage,
      limit: 12,
    }
    if (filters.search) params.search = filters.search
    if (filters.taxonomyIds.length > 0) params.taxonomy_ids = filters.taxonomyIds.join(',')
    if (filters.documentTypes.length > 0) params.document_types = filters.documentTypes.join(',')
    return params
  }, [filters])

  const fetchDocuments = useCallback(async (currentPage: number, append = false) => {
    try {
      setLoading(true)
      setError(null)

      const response = await libraryService.listDocuments(buildParams(currentPage))

      if (append) {
        setDocuments(prev => [...prev, ...response.data])
      } else {
        setDocuments(response.data)
      }

      setTotalPages(response.pagination.total_pages)
    } catch (err) {
      setError('Erro ao carregar biblioteca')
      console.error('Library error:', err)
    } finally {
      setLoading(false)
    }
  }, [buildParams])

  const fetchTaxonomies = useCallback(async () => {
    try {
      const data = await libraryService.getTaxonomies()
      setTaxonomies(data)
    } catch (err) {
      console.error('Error loading taxonomies:', err)
    }
  }, [])

  useEffect(() => {
    fetchTaxonomies()
  }, [fetchTaxonomies])

  useEffect(() => {
    setPage(1)
    fetchDocuments(1, false)
  }, [fetchDocuments])

  const toggleTaxonomy = (id: string) => {
    setFilters(prev => ({
      ...prev,
      taxonomyIds: prev.taxonomyIds.includes(id)
        ? prev.taxonomyIds.filter(t => t !== id)
        : [...prev.taxonomyIds, id],
    }))
  }

  const toggleDocumentType = (type: string) => {
    setFilters(prev => ({
      ...prev,
      documentTypes: prev.documentTypes.includes(type)
        ? prev.documentTypes.filter(t => t !== type)
        : [...prev.documentTypes, type],
    }))
  }

  const setSearch = (search: string | undefined) => {
    setFilters(prev => ({ ...prev, search }))
  }

  const clearFilters = () => {
    setFilters({ taxonomyIds: [], documentTypes: [] })
  }

  const loadMore = () => {
    if (page < totalPages) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchDocuments(nextPage, true)
    }
  }

  const refresh = () => {
    setPage(1)
    fetchDocuments(1, false)
  }

  return {
    documents,
    loading,
    error,
    page,
    totalPages,
    hasMore: page < totalPages,
    filters,
    taxonomies,
    toggleTaxonomy,
    toggleDocumentType,
    setSearch,
    clearFilters,
    loadMore,
    refresh,
  }
}
